import { CATEGORY, DATES, FORMATTING, JARGON, LAYOUT } from "../modules/constants"
import { formatFloat, formatInteger, getBirthYear, nundefined, yearsToString } from "../modules/utilities"
import { LEGEND } from "../modules/legends"

const { birthYear, black, date, float, left, integer, local, number, percent, right, string, yearsMonths } = FORMATTING

export const headCell = props => {
  const {
    align = right,
    borderLeft = null,
    cell,
    colSpan = 1,
    id,
    paddingLeft = 0,
    paddingRight = LAYOUT.HOME.PADDING.RIGHT,
    sortLabel = true,
    width = null,
  } = props

  const label = props.label ?? LEGEND[id]?.label ?? ""
  const tooltipTitle = props.tooltipTitle ?? LEGEND[id]?.tooltipTitle ?? ""

  // Once we have used cell for all tables using this function, we will no longer need to return the object
  if (cell)
    cell[id] = { align, borderLeft, colSpan, id, label, paddingLeft, paddingRight, sortLabel, tooltipTitle, width }

  return { align, borderLeft, colSpan, id, label, paddingLeft, paddingRight, sortLabel, tooltipTitle, width }
}

export const footCell = props => {
  const {
    align = right,
    borderLeft = null,
    cell,
    colSpan = 1,
    id,
    label = "",
    paddingLeft = 0,
    paddingRight = LAYOUT.HOME.PADDING.RIGHT,
    sortLabel = true,
    tooltipTitle = "",
    width = null,
  } = props

  // Once we have used cell for all tables using this function, we will no longer need to return the object
  if (cell)
    cell[id] = { align, borderLeft, colSpan, id, label, paddingLeft, paddingRight, sortLabel, tooltipTitle, width }

  return { align, borderLeft, colSpan, id, label, paddingLeft, paddingRight, sortLabel, tooltipTitle, width }
}

export const getCellWithTooltip = props => {
  // console.log('getCellWithTooltip props:',props)

  const {
    backgroundColor,
    borderLeft,
    color = black,
    colSpan = 1,
    link,
    newTab = false, // open link in new tab by default if link is provided
    paddingLeft = 0,
    paddingRight = LAYOUT.HOME.PADDING.RIGHT,
    prefix,
    tooltip = props.tooltip ?? "",
    type = number,
    value,
  } = props

  // align defaults to 'left' if type is 'string', otherwise 'right'
  const align = props.align ?? (type === string ? left : right)

  // Assign sortValue as value if undefined
  const sortValue = props.sortValue === undefined ? value : props.sortValue

  return getTableCell({ align, backgroundColor, borderLeft, color, colSpan, link, newTab, paddingLeft, paddingRight, prefix, sortValue, tooltip, type, value })
}

const getTableCell = props => {
  let { type, value, prefix } = props

  switch (type) {
    case date:
      const futureTest = value && (value.getTime() === DATES.FUTURE.getTime())
      return (nundefined(value) || futureTest)
        ? { ...props, type: string, value: JARGON.UNKNOWN, tooltip: null } : processDate(props)

    case number:
      return value === null ? { ...props, value: JARGON.UNKNOWN, sortValue: 0 } : props

    case string:
      return nundefined(value) ? { ...props, value: JARGON.UNKNOWN, sortValue: "~" } : props
    // '~' is very high ASCII value, so it sorts to the end

    case local:
      return nundefined(value) ? { ...props, value, sortValue: Number.NEGATIVE_INFINITY } : props

    case percent:
      return nundefined(value) ? { ...props, sortValue: Number.NEGATIVE_INFINITY } : { ...props, value: `${value}%` }

    default:
      return prefix ? { ...props, type: string, value: `${prefix} ${value}` } : props
  }
}

const processDate = props => {
  const { value } = props
  const year = value.getFullYear()
  const month = value.getMonth()

  return year === DATES.FUTURE_YEAR
    ? { ...props, type: string, value: JARGON.UNKNOWN }
    : (year === DATES.TIME_START_YEAR && month === 0)
      ? { ...props, type: string, value: JARGON.UNKNOWN }
      : (year === DATES.UNKNOWN_FROM_YEAR && month === DATES.UNKNOWN_FROM_MONTH)
        ? { ...props, type: string, value: JARGON.CH_UNKNOWN_START_DATE } : props
}

export const calcStats = stats => {
  // console.log("stats: ", stats)
  if (stats.numof > 0)
    stats.mean = stats.total / stats.numof
}

export const setStats = (value, stats, countZero) => {
  // If value is a boolean, we add 1 or 0 to total
  // Note that unary '+' operator converts boolean into a number ie true equals 1
  if (typeof value == "boolean") {
    // NB: += works even if total is null
    stats.total += value

    // If we count zeros
    if (countZero)
      ++stats.numof

  } else  // If value is a number and..
    if (typeof value == "number") {
      stats.total += value

      // If count zeros or it's not zero. NB: += works even if total is null
      if (countZero || value !== 0)
        ++stats.numof
    }
}

export const firstToLast = name => {
  const split = name.split(', ')

  return split.length > 1
    ? `${split[1]} ${split[0]}`
    : name
}

export const addPaddingRightToLastCell = cells => {
  if (cells.length > 0)
    cells[cells.length - 1].paddingRight = LAYOUT.HOME.PADDING.BORDER
}

export const getRowIcon = title => hasLowerCase(title) ? CATEGORY.PERSON : CATEGORY.COMPANY

const hasLowerCase = str => (str ? str.toUpperCase() !== str : false)

export const getCell = props => {
  // console.log('getCell props:', props)

  const { align, borderLeft, column, paddingLeft, tooltip, value } = props
  const label = value === undefined // We have not supplied value so use the label
    ? props.label ?? ""
    : value === null // 'value' has never been assigned so use "n/a" (JARGON.UNKNOWN)
      ? JARGON.UNKNOWN
      : formatIt(props) // 'value' has been assigned (possibly 0!) so format it

  return headCell({
    align,
    borderLeft,
    id: column,
    label,
    paddingLeft,
    tooltipTitle: tooltip,
  })
}

const formatIt = ({ prefix = "", suffix = "", type = float, precision = 1, value }) => {
  switch (type) {
    case birthYear:
      return `${prefix}${getBirthYear(value)}${suffix}`

    case float:
      return `${prefix}${formatFloat(value, precision)}${suffix}`

    case percent:
      return `${prefix}${formatFloat(value * 100, precision)}${suffix}`

    case integer:
      return `${prefix}${formatInteger(value)}${suffix}`

    case yearsMonths:
      return `${prefix}${yearsToString(value)}${suffix}`

    default:
      return `${prefix}${value}${suffix}`
  }
}
